import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useDebounce } from "react-use";

import { Filter } from "@/components/molecules";
import { handleHighlight } from "@/core/html";
import { searchApi } from "@/shared/apis";
import { SearchContext, SearchResult } from "@/shared/apis/searchApi";
import { useAuth } from "@/shared/hooks";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

import { getFilters } from "./getFilters";

export const searchHighlightClassName = "searchHighlightText";

export enum Display {
    TYPE_3_CHARS,
    LOADING,
    RESULTS,
    NO_RESULTS,
    ERROR,
}

type useSearchProps = {
    message: string;
    isBusy: boolean;
    filters: Filter[];
    results: SearchResult[];
    searchContext: SearchContext;
    setSearchWord: React.Dispatch<React.SetStateAction<string>>;
    setSearchContext: React.Dispatch<React.SetStateAction<SearchContext>>;
};

const useSearch = ({ isMobile }: { isMobile: boolean }): useSearchProps => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { policyholders } = usePolicyholders();
    const [searchContext, setSearchContext] = useState<SearchContext>(SearchContext.All);
    const [searchWord, setSearchWord] = useState("");
    const [debouncedSearchWord, setDebouncedSearchWord] = useState("");

    const filters = getFilters(user, policyholders);

    useDebounce(
        () => {
            if (searchWord !== debouncedSearchWord) {
                if (searchWord.length > 2) {
                    setDebouncedSearchWord(searchWord);
                } else {
                    setDebouncedSearchWord("");
                }
            }
        },
        400,
        [searchWord],
    );

    const {
        data: results = [],
        isFetching,
        isError,
    } = useQuery(
        ["searchResults", { debouncedSearchWord, searchContext, policyholders }],
        () =>
            searchApi.getSearchResult({
                term: debouncedSearchWord,
                searchContext,
                policyholders,
                user,
            }),
        {
            enabled: debouncedSearchWord !== "",
            suspense: false,
            useErrorBoundary: false,
        },
    );

    useEffect(() => {
        if (!isFetching) {
            // Highlight word after query is done
            handleHighlight(debouncedSearchWord, true, searchHighlightClassName);
        }
    }, [isFetching, debouncedSearchWord]);

    let currentDisplay = Display.RESULTS;
    if (isError) {
        currentDisplay = Display.ERROR;
    } else if (debouncedSearchWord.length < 3) {
        currentDisplay = Display.TYPE_3_CHARS;
    } else if (isFetching) {
        currentDisplay = Display.LOADING;
    } else if (results.length === 0) {
        currentDisplay = Display.NO_RESULTS;
    }

    const getMessage = (display: Display): string => {
        switch (display) {
            case Display.TYPE_3_CHARS:
                return t("Common.StartSearchMinimunLetters", { number: 3 });
            case Display.LOADING:
                return t("Common.Loading");
            case Display.NO_RESULTS:
                return t("Common.NoResults");
            case Display.ERROR:
                return t("Errors.Title");
            case Display.RESULTS:
                return isMobile ? t("Common.XResults", { number: results.length }) : null;
            default:
                return null;
        }
    };

    return {
        filters,
        results,
        setSearchWord,
        searchContext,
        setSearchContext,
        isBusy: isFetching,
        message: getMessage(currentDisplay),
    };
};

export default useSearch;
